import { ElevateConfig } from '##/dataModels/config';
import { Category, Genre, Item } from '##/dataModels/item';
import { Movie } from '##/dataModels/movie';
import { Show } from '##/dataModels/show';
import { ShortFilm } from '##/dataModels/shortFilm';
import { Route } from '##/dataModels/route';
import { PageLayout } from '##/dataModels/pageLayout';
import { MenuItem } from '##/dataModels/menuItem';
import { Episode } from '##/dataModels/episode';
import { User } from '##/dataModels/user';
import { Season } from '##/dataModels/season';
import { DeviceInfo } from '##/dataModels/deviceInfo';
import { Entitlement, Subscription } from '##/dataModels/subscription';
import { MediaItem } from '##/dataModels/mediaItem';
import { Message } from '##/dataModels/message';
import {
  ContinueWatchingShowResponse,
  PlaybackActionHelperProps,
  PlaybackResponse,
  PlayPosition,
} from '##/dataModels/playback';

export interface ConfigService {
  getConfig(segment?: string): Promise<ElevateConfig>;
  // getApplicationConfig(segment?: string): Promise<ApplicationConfig>;
  // getFeatureFlags(segment?: string): Promise<FeatureFlagsConfig>;
  // getProvidersConfig(segment?: string): Promise<ProvidersConfig>;
}

export interface OVPOptions {
  pageSize?: number; // defaults to 20
  /**
   * Accedo OVP supports availableDate | publishedDate.
   * For getCategories it supports id | title.
   */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  pageNumber?: number; // defaults to 1
}

export interface OVPLiveOptions extends OVPOptions {
  startTime: number;
  endTime: number;
  channelId?: string;
}

export interface EpisodeOptions extends OVPOptions {
  showId?: string;
  seasonId?: string;
  url?: string; // Direct URL for fetching episodes
}

export interface SearchOptions extends OVPOptions {
  itemTypes?: string[]; // defaults to ['movie', 'tvshow']
  abortControllerSignal?: AbortSignal;
}

export interface OVPPaginationResponse<ItemType extends Item> {
  id: string;
  type: string;
  pageSize?: number;
  pageNumber?: number;
  totalCount: number;
  items: ItemType[];
}

export interface OVPSearchResponse {
  movies?: Movie[];
  shows?: Show[];
  shortFilms?: ShortFilm[];
}

export interface OVPService {
  search(keyword: string, options?: SearchOptions, authToken?: string): Promise<OVPSearchResponse>;
  searchByType(keyword: string, itemType: string, options?: OVPOptions): Promise<OVPPaginationResponse<Item>>;
  getAssetsByCategory(categoryId: string, opts?: OVPOptions): Promise<OVPPaginationResponse<MediaItem>>;
  getMovies(options?: OVPOptions): Promise<OVPPaginationResponse<Movie>>;
  getMovie(id: string, authToken?: string): Promise<Movie>;
  getShortFilm(id: string, authToken?: string): Promise<ShortFilm>;
  getShow(id: string, authToken?: string): Promise<Show>;
  getSeasons(
    showId: string,
    options?: OVPOptions,
    authToken?: string,
    seasonsUrl?: string,
  ): Promise<OVPPaginationResponse<Season>>;
  getEpisode(id: string, authToken?: string): Promise<Episode>;
  getEpisodes(
    options?: EpisodeOptions,
    authToken?: string,
    clearCache?: boolean,
  ): Promise<OVPPaginationResponse<Episode>>;
  getSeason(seasonId: string): Promise<Season>;
  getShows(options?: OVPOptions): Promise<OVPPaginationResponse<Show>>;
  getCategories(): Promise<OVPPaginationResponse<Category>>;
  getCategoryById(categoryId: string): Promise<Category | undefined>;
  getGenres(): Promise<{ genres: Genre[]; totalCount: number }>;
  getAsset(assetId: string): Promise<Item>;
  getPlaylist(listId: string, options?: OVPOptions): Promise<OVPPaginationResponse<MediaItem>>;
  fetch(url: string, options?: OVPOptions, authToken?: string): Promise<OVPPaginationResponse<Item>>;
  addToMyList(itemId: string, itemType: string, token: string): Promise<boolean>;
  removeFromMyList(itemId: string, token: string): Promise<boolean>;
}

export interface CmsService {
  getRoutes(segment?: string): Promise<Route[]>;
  getPageLayout(route: string, segment?: string): Promise<PageLayout>;
  getMenu(segment?: string): Promise<MenuItem[]>;
  getDefaultTheme(segment?: string): Promise<string>; // TODO: [BUILDELEVATE-2221] update
}

export interface LoginRequest {
  username: string;
  password: string;
  email: string;
  name?: string;
  country?: string;
  phoneNumber?: string;
  deviceDetails?: DeviceInfo;
}

export interface RegisterRequest {
  username: string;
  password: string;

  /* TODO can remove all below as not currently supported on MW */
  email: string;
  name?: string;
  country?: string;
  phoneNumber?: string;
  deviceDetails?: DeviceInfo;
}

export interface IDPService {
  getUser(): User | null;
  getUserDetails({ token }: { token: string }): Promise<User>;
  login(loginRequest: LoginRequest, segment?: string): Promise<User>;
  logout({ token, segment }: { token?: string; segment?: string }): Promise<void>;
  refresh({ token, segment }: { token: string; segment?: string }): Promise<User>;
  register(registerRequest: RegisterRequest, segment?: string): Promise<User>;
}

export interface SubscriptionService {
  getSubscriptions({ authToken }: { authToken: string }): Promise<Subscription[]>;
  getEntitlements({ authToken }: { authToken: string }): Promise<Entitlement[]>;
  clearSubscriptionsCache(): Promise<void>;
}

export interface MessagingService {
  addEventListener(identifier: string, listener: (data: Message) => void, eventName?: string): void;
  removeEventListener(identifier: string, listener?: (data: Message) => void): void;
  destroy(): void;
}
export interface PlaybackService {
  getContinueWatchingData({ token, clearCache }: { token: string; clearCache?: boolean }): Promise<MediaItem[]>;
  getContinueWatchingByShowId({
    showId,
    token,
  }: {
    showId: string;
    token: string;
  }): Promise<ContinueWatchingShowResponse>;
  getContinueWatchingBySeasonId({ seasonId, token }: { seasonId: string; token: string }): Promise<MediaItem[]>;
  getPlayPositionByAssetId({ assetId, token }: { assetId: string; token: string }): Promise<PlayPosition>;
  play(args: PlaybackActionHelperProps): Promise<PlaybackResponse | null>;
  pause(args: PlaybackActionHelperProps): Promise<PlaybackResponse | null>;
  resume(args: PlaybackActionHelperProps): Promise<PlaybackResponse | null>;
  stop(args: PlaybackActionHelperProps): Promise<PlaybackResponse | null>;
  progress(args: PlaybackActionHelperProps): Promise<PlaybackResponse | null>;
}

export interface ServiceRegistry {
  ovp: { provider: OVPService };
  idp?: { provider: IDPService };
  config: { provider: ConfigService };
  cms: { provider: CmsService };
  subscriptions?: { provider: SubscriptionService };
  playback?: { provider: PlaybackService };
}

export enum ServiceTypes {
  ovp = 'ovp',
  idp = 'idp',
  config = 'Config',
  cms = 'cms',
  subscriptions = 'subscriptions',
  playback = 'playback',
}

export type ServiceTypesKeys = keyof typeof ServiceTypes;
